import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Euro, FileText, ShoppingCart, Target, AlertTriangle, ArrowUpRight,
  Cpu, Activity, Info, TriangleAlert
} from "lucide-react";
import api from "../../lib/api";

const SEVERITY = {
  alert: { border: "border-red-500/50",    text: "text-red-300",    bg: "bg-red-500/[0.06]",    dot: "bg-red-400",    icon: AlertTriangle },
  warn:  { border: "border-amber-500/50",  text: "text-amber-300",  bg: "bg-amber-500/[0.06]",  dot: "bg-amber-400",  icon: TriangleAlert },
  info:  { border: "border-cyan-500/50",   text: "text-cyan-300",   bg: "bg-cyan-500/[0.06]",   dot: "bg-cyan-400",   icon: Info },
};

function KpiTile({ icon: Icon, label, value, sub, accent = "cyan", testid }) {
  const colorMap = {
    cyan: "text-cyan-400",
    amber: "text-amber-400",
    emerald: "text-emerald-400",
    red: "text-red-400",
    slate: "text-slate-200",
  };
  return (
    <div
      className="corner-bracket bg-slate-950/60 border border-slate-800 px-4 py-3.5 rounded-sm hover:border-cyan-500/40 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-slate-500">{label}</span>
        <Icon size={13} className="text-slate-600"/>
      </div>
      <div className={`font-mono text-2xl ${colorMap[accent] || colorMap.cyan}`}>{value}</div>
      {sub && <div className="mt-1 font-mono text-[10px] tracking-wider text-slate-500">{sub}</div>}
    </div>
  );
}

function MachineBar({ m }) {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, m.utilization_pct || 0));
  const barColor = pct >= 75 ? "bg-emerald-400" : pct >= 35 ? "bg-cyan-400" : "bg-slate-600";
  return (
    <div className="px-3 py-2.5 border-b border-slate-800 last:border-b-0">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm text-slate-200 font-medium truncate pr-2" title={m.name}>{m.name}</div>
        <div className="font-mono text-xs text-cyan-400 shrink-0">{pct}%</div>
      </div>
      <div className="h-1 bg-slate-900 overflow-hidden rounded-sm">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}/>
      </div>
      <div className="mt-1 flex items-center justify-between font-mono text-[9px] tracking-wider text-slate-500">
        <span>{t("missionControl.quotesRate", { count: m.quotes_this_month, rate: m.hourly_rate })}</span>
        <span className="uppercase">{m.category_id?.replace(/_/g, " ") || ""}</span>
      </div>
    </div>
  );
}

function AlertCard({ a }) {
  const s = SEVERITY[a.severity] || SEVERITY.info;
  const Icon = s.icon;
  return (
    <Link
      to={a.link || "/dashboard"}
      className={`block border ${s.border} ${s.bg} rounded-sm px-3 py-2.5 hover:bg-slate-900/60 transition-colors group`}
      data-testid={`alert-${a.code}`}
    >
      <div className="flex items-start gap-2.5">
        <Icon size={14} className={`${s.text} mt-0.5 shrink-0`}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">[{a.code}]</div>
            <ArrowUpRight size={12} className="text-slate-600 group-hover:text-cyan-400 transition-colors"/>
          </div>
          <div className={`mt-1 text-sm font-medium ${s.text}`}>{a.title}</div>
          <div className="mt-0.5 text-xs text-slate-500 leading-snug">{a.body}</div>
        </div>
      </div>
    </Link>
  );
}

export default function MissionControlHeader() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/overview");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const i = setInterval(load, 60000); // 60s refresh
    const c = setInterval(() => setTick((x) => x + 1), 1000); // header clock
    return () => { cancelled = true; clearInterval(i); clearInterval(c); };
  }, []);

  const ts = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  if (loading) {
    return (
      <div className="mb-8 border border-slate-800 rounded-sm bg-slate-950/60 px-5 py-4 font-mono text-xs tracking-wider text-slate-500">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-live mr-2"/>
        {t("missionControl.booting")}
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis;
  const profitableTone = k.avg_margin_pct >= 20;

  return (
    <div className="mb-8" data-testid="mission-control">
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-2 border border-slate-800 border-b-0 bg-slate-900/60 font-mono text-[10px] tracking-[0.22em] uppercase rounded-sm rounded-b-none">
        <div className="flex items-center gap-3 text-slate-400">
          <span className="inline-flex items-center gap-2 text-cyan-400">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-live"/> MC_DASHBOARD_v1.0
          </span>
          <span className="hidden sm:inline text-slate-600">·</span>
          <span className="hidden sm:inline">USER: {(data.kpis ? "ONLINE" : "OFFLINE")}</span>
        </div>
        <div className="flex items-center gap-3 text-slate-500">
          <span className="hidden md:inline">REFRESH 60s</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-300">{ts}</span>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-slate-800 border-t-0 divide-x divide-y lg:divide-y-0 divide-slate-800">
        <KpiTile
          icon={FileText}
          label={t("missionControl.kpiQuotes")}
          value={k.quotes_month}
          sub={t("missionControl.sentAccepted", { sent: data.status_breakdown.sent, accepted: data.status_breakdown.accepted })}
          testid="kpi-quotes-month"
        />
        <KpiTile
          icon={Euro}
          label={t("missionControl.kpiValue")}
          value={`€ ${(k.value_month_eur || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`}
          sub={t("missionControl.totalQuoted")}
          testid="kpi-value-month"
        />
        <KpiTile
          icon={Target}
          label={t("missionControl.kpiAvgMargin")}
          value={`${k.avg_margin_pct}%`}
          sub={profitableTone ? t("missionControl.profitable") : t("missionControl.watchMargin")}
          accent={profitableTone ? "emerald" : "amber"}
          testid="kpi-avg-margin"
        />
        <KpiTile
          icon={AlertTriangle}
          label={t("missionControl.kpiLowMargin")}
          value={k.low_margin_count}
          sub={t("missionControl.belowThreshold")}
          accent={k.low_margin_count > 0 ? "red" : "slate"}
          testid="kpi-low-margin"
        />
        <KpiTile
          icon={ShoppingCart}
          label={t("missionControl.kpiRfqs")}
          value={k.rfqs_active}
          sub={t("missionControl.rfqsSub", { total: k.rfqs_open_global, offers: k.recent_offers })}
          testid="kpi-rfqs"
        />
        <KpiTile
          icon={Activity}
          label={t("missionControl.kpiUncalibrated")}
          value={k.pending_calibration}
          sub={t("missionControl.last30")}
          accent={k.pending_calibration >= 3 ? "amber" : "slate"}
          testid="kpi-uncalibrated"
        />
      </div>

      {/* Two-column panels: Machine utilization + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        <div className="lg:col-span-2 border border-slate-800 rounded-sm bg-slate-950/60" data-testid="machine-utilization">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 font-mono text-[10px] tracking-[0.22em] uppercase">
            <span className="text-cyan-400 inline-flex items-center gap-2">
              <Cpu size={11}/> {t("missionControl.utilization")}
            </span>
            <span className="text-slate-500">{t("missionControl.top4")}</span>
          </div>
          {data.machine_utilization && data.machine_utilization.length > 0 ? (
            data.machine_utilization.map((m) => <MachineBar key={m.machine_id} m={m}/>)
          ) : (
            <div className="px-3 py-6 text-center">
              <div className="text-sm text-slate-400">{t("missionControl.noMachines")}</div>
              <Link to="/machines" className="mt-2 inline-block text-xs text-cyan-400 hover:underline font-mono tracking-wider">{t("missionControl.addMachines")}</Link>
            </div>
          )}
        </div>

        <div className="border border-slate-800 rounded-sm bg-slate-950/60" data-testid="production-alerts">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 font-mono text-[10px] tracking-[0.22em] uppercase">
            <span className="text-cyan-400 inline-flex items-center gap-2">
              <AlertTriangle size={11}/> {t("missionControl.alerts")}
            </span>
            <span className="text-slate-500">{data.alerts.length}</span>
          </div>
          <div className="p-2 space-y-2 max-h-72 overflow-y-auto">
            {data.alerts.length === 0 ? (
              <div className="px-2 py-6 text-center">
                <div className="text-sm text-emerald-400 font-mono">{t("missionControl.allNominal")}</div>
                <div className="text-xs text-slate-500 mt-1">{t("missionControl.noAlerts")}</div>
              </div>
            ) : data.alerts.map((a) => <AlertCard key={a.code} a={a}/>)}
          </div>
        </div>
      </div>
    </div>
  );
}
